import { useEffect, useState } from "react";
import { Save } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

export default function PathwaysAdmin() {
  const [pathways, setPathways] = useState([]);
  const [active, setActive] = useState(null);
  const [form, setForm] = useState({ name: "", description: "", steps: "", documents: "" });
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/visa/pathways").then(({ data }) => setPathways(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const select = (p) => {
    setActive(p);
    setForm({
      name: p.name, description: p.description,
      steps: p.steps.join("\n"), documents: p.documents.join("\n"),
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/admin/visa/pathways/${active.key}`, {
        name: form.name,
        description: form.description,
        steps: form.steps.split("\n").map((s) => s.trim()).filter(Boolean),
        documents: form.documents.split("\n").map((s) => s.trim()).filter(Boolean),
      });
      toast.success("Parcours mis à jour");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <h2 className="font-head text-2xl font-extrabold text-primary mb-4">Parcours visa</h2>
      <div className="grid sm:grid-cols-2 gap-3 mb-6">
        {pathways.map((p) => (
          <button key={p.key} onClick={() => select(p)} data-testid={`pathway-edit-${p.key}`}
            className={`text-left p-4 rounded-2xl border soft-shadow ${active?.key === p.key ? "border-accent bg-accent/5" : "border-gray-100 bg-white"}`}>
            <h3 className="font-semibold text-primary">{p.name}</h3>
            <p className="text-xs text-muted-foreground">{p.documents.length} pièce(s) · {p.steps.length} étape(s)</p>
          </button>
        ))}
      </div>

      {active && (
        <div className="bg-white rounded-2xl soft-shadow border border-gray-100 p-5 space-y-3" data-testid="pathway-edit-form">
          <h3 className="font-head font-bold text-primary">Modifier : {active.name}</h3>
          <div>
            <label className="text-sm font-semibold text-primary">Nom</label>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-xl mt-1" data-testid="pathway-name-input" />
          </div>
          <div>
            <label className="text-sm font-semibold text-primary">Description</label>
            <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} className="rounded-xl mt-1" data-testid="pathway-desc-input" />
          </div>
          <div>
            <label className="text-sm font-semibold text-primary">Étapes (une par ligne)</label>
            <Textarea value={form.steps} onChange={(e) => setForm({ ...form, steps: e.target.value })} rows={7} className="rounded-xl mt-1" data-testid="pathway-steps-input" />
          </div>
          <div>
            <label className="text-sm font-semibold text-primary">Pièces à fournir (une par ligne)</label>
            <Textarea value={form.documents} onChange={(e) => setForm({ ...form, documents: e.target.value })} rows={6} className="rounded-xl mt-1" data-testid="pathway-docs-input" />
          </div>
          <Button onClick={save} disabled={saving} className="rounded-full bg-accent text-accent-foreground hover:bg-accent/90 font-bold" data-testid="save-pathway-btn">
            <Save className="h-4 w-4 mr-1" /> {saving ? "Enregistrement..." : "Enregistrer"}
          </Button>
        </div>
      )}
    </div>
  );
}
